import {
	createFileRoute,
	Link,
	notFound,
	redirect,
} from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { ChangeLogCard } from "@/components/cards/change-log-card";
import { ChangelogTypeFilter } from "@/components/misc/ChangeLogFilter";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { config } from "@/data/config";
import { changelogEmptyStateClass } from "@/lib/changelog-type-styles";
import { isModuleEnabled } from "@/lib/modules";
import { getStaggeredAnimationClasses } from "@/utils/animation";
import { changelogsByVersion } from "@/utils/change-log";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_marketing/changelog/$version")({
	component: RouteComponent,
	beforeLoad: ({ params }) => {
		if (!isModuleEnabled("marketing_changelog")) {
			throw redirect({ to: "/" });
		}
		const parsed = z.string().safeParse(params.version);
		const versionChangelogs = parsed.success
			? changelogsByVersion[parsed.data]
			: undefined;
		if (!versionChangelogs || versionChangelogs.length === 0) {
			// Unknown versions are a 404, not a redirect to the index.
			throw notFound();
		}
		return { version: parsed.success ? parsed.data : "" };
	},
	head: ({ params: { version } }) => {
		const releaseDate = getChangelogHeadReleaseDate(
			version,
			changelogsByVersion,
		);

		return {
			...seo({
				title: `Changelog - ${version}`,
				description: `Here you can find the changes for version ${version}. This was released on ${releaseDate}.`,
				keywords:
					"changelog, updates, changes, bug fixes, improvements, features",
			}),
		};
	},
});

export function filterChangelogsByType<T extends { type: string }>(
	changelogs: T[],
	selectedType: string,
): T[] {
	if (selectedType === "all") {
		return changelogs;
	}
	return changelogs.filter((changelog) => changelog.type === selectedType);
}

export function getChangelogHeadReleaseDate(
	version: string,
	byVersion: Record<string, Array<{ date?: string | Date }>>,
): string {
	const versionChangelogs = byVersion[version];
	const releaseDate = versionChangelogs?.[0]?.date;
	if (!releaseDate) {
		return "unknown date";
	}
	const normalizedDate =
		releaseDate instanceof Date ? releaseDate.toISOString() : releaseDate;
	return new Date(normalizedDate).toLocaleDateString();
}

export function formatChangelogReleaseDate(
	date: string | Date | undefined,
): string {
	const normalizedDate =
		date instanceof Date
			? date.toISOString()
			: (date ?? new Date().toISOString());
	return new Date(normalizedDate).toLocaleDateString();
}

export function RouteComponent() {
	const [selectedType, setSelectedType] = useState<string>("all");
	const { version } = Route.useParams();

	// Get changelogs for the current version param
	const versionChangelogs = changelogsByVersion[version] || [];

	const filteredVersionChangelogs = filterChangelogsByType(
		versionChangelogs,
		selectedType,
	);

	const releaseDate = formatChangelogReleaseDate(versionChangelogs[0]?.date);

	return (
		<MarketingShell>
			<section className="pt-20 pb-16 sm:pt-28 sm:pb-24">
				<MarketingContainer>
					<MarketingHeader
						title="Changelog"
						heading={`Version ${version}`}
						description={`Shipped on ${releaseDate}. Questions about this release? Email <a href="mailto:${config.supportEmail}" class="font-medium text-primary hover:underline">${config.supportEmail}</a>.`}
						descriptionHtml
					/>

					<div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
						<ChangelogTypeFilter
							selectedType={selectedType}
							onSelectType={setSelectedType}
						/>
						<Link
							to="/changelog"
							className="text-sm font-medium text-primary hover:underline"
						>
							← All releases
						</Link>
					</div>

					<div className="flex flex-col gap-6">
						{filteredVersionChangelogs.length === 0 ? (
							<div className={changelogEmptyStateClass}>
								No changelogs match this filter for version {version}.
							</div>
						) : (
							filteredVersionChangelogs.map((changelog, index) => (
								<div
									key={changelog._meta.path}
									className={getStaggeredAnimationClasses(index)}
								>
									<ChangeLogCard changelog={changelog} />
								</div>
							))
						)}
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
